import React, { useEffect, useState } from 'react';
import api from '../services/api';
import SamlConfigPanel from '../components/SamlConfigPanel';

/**
 * Identity Console — super-admin-only cross-group view of SAML SSO configs.
 *
 * SSO trust is per Athena tenant, and every Athena tenant maps to one
 * practice_group in NH. This console lists practice_groups and their SSO
 * state; picking a group opens the SamlConfigPanel inline for editing.
 *
 * Mounted at /admin/identity by SuperAdminLayout (super_admin only).
 */
function IdentityConsole() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeGroupId, setActiveGroupId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.request('/admin/saml/configs');
      if (res.success) setRows(res.data || []);
      else setError(res.error || 'Failed to load SSO configurations');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const formatTime = (s) => {
    if (!s) return '—';
    try {
      return new Date(s).toLocaleString();
    } catch { return s; }
  };

  const StatusBadge = ({ row }) => {
    if (!row.config_id) {
      return <span style={styles.badgeMuted}>Not configured</span>;
    }
    if (row.is_enabled) {
      return <span style={styles.badgeOk}>Enabled</span>;
    }
    return <span style={styles.badgeWarn}>Disabled</span>;
  };

  const groupsWithPractices = rows.filter(r => (r.practices || []).some(p => p.enabled));
  const configuredCount = groupsWithPractices.filter(r => r.config_id).length;
  const enabledCount = groupsWithPractices.filter(r => r.is_enabled).length;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Identity & SSO</h1>
          <p style={styles.subtitle}>
            Manage SAML 2.0 single sign-on per practice group. {enabledCount} of {groupsWithPractices.length} group{groupsWithPractices.length === 1 ? '' : 's'} have SSO active.
          </p>
        </div>
        <button style={styles.refreshBtn} onClick={load} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      <div style={styles.statsRow}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{groupsWithPractices.length}</div>
          <div style={styles.statLabel}>Practice groups</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{configuredCount}</div>
          <div style={styles.statLabel}>SSO configured</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{enabledCount}</div>
          <div style={styles.statLabel}>SSO enabled</div>
        </div>
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Practice Group</th>
              <th style={styles.th}>Practices</th>
              <th style={styles.th}>SSO Status</th>
              <th style={styles.th}>IdP Entity ID</th>
              <th style={styles.th}>Auto-provision</th>
              <th style={styles.th}>Default role</th>
              <th style={styles.th}>Updated</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={8} style={styles.tdEmpty}>Loading...</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} style={styles.tdEmpty}>No practice groups yet.</td></tr>
            )}
            {rows.map(row => {
              const enabledPractices = (row.practices || []).filter(p => p.enabled);
              if (enabledPractices.length === 0) return null;
              const isActive = activeGroupId === row.practice_group_id;
              return (
                <React.Fragment key={row.practice_group_id}>
                  <tr style={isActive ? styles.trActive : undefined}>
                    <td style={styles.td}><strong>{row.group_name}</strong></td>
                    <td style={styles.td}>
                      <span style={{ fontSize: 12 }}>
                        {enabledPractices.map(p => p.name.trim()).join(', ')}
                        <span style={styles.practiceCountBadge}>{enabledPractices.length}</span>
                      </span>
                    </td>
                    <td style={styles.td}><StatusBadge row={row} /></td>
                    <td style={styles.tdMono}>{row.idp_entity_id || '—'}</td>
                    <td style={styles.td}>{row.config_id ? (row.auto_provision_users ? 'On' : 'Off') : '—'}</td>
                    <td style={styles.td}>{row.default_role || '—'}</td>
                    <td style={styles.td}>{formatTime(row.updated_at)}</td>
                    <td style={styles.td}>
                      <button
                        style={isActive ? styles.btnSecondaryActive : styles.btnSecondary}
                        onClick={() => setActiveGroupId(isActive ? null : row.practice_group_id)}
                      >
                        {isActive ? 'Close' : (row.config_id ? 'Edit' : 'Configure')}
                      </button>
                    </td>
                  </tr>
                  {isActive && (
                    <tr>
                      <td colSpan={8} style={styles.editorCell}>
                        <div style={styles.editorBreadcrumb}>
                          SSO › {row.group_name}
                        </div>
                        <SamlConfigPanel practiceGroupId={row.practice_group_id} groupName={row.group_name} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  page: { padding: '24px 32px', maxWidth: 1400 },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 16,
  },
  title: { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  subtitle: { fontSize: 13, color: '#64748b', margin: '4px 0 0' },
  refreshBtn: {
    padding: '8px 16px', background: '#fff', color: '#334155',
    border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer',
  },
  errorBanner: {
    padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca',
    borderRadius: 8, color: '#dc2626', fontSize: 13, marginBottom: 16,
  },
  statsRow: { display: 'flex', gap: 16, marginBottom: 24 },
  statCard: {
    flex: 1, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
    padding: '16px 20px',
  },
  statValue: { fontSize: 26, fontWeight: 700, color: '#0f172a' },
  statLabel: { fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 },
  tableWrap: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '10px 14px', background: '#f8fafc',
    color: '#475569', fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: 0.4, borderBottom: '1px solid #e2e8f0',
  },
  td: { padding: '12px 14px', color: '#1e293b', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
  tdMono: {
    padding: '12px 14px', color: '#475569', borderBottom: '1px solid #f1f5f9',
    fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', maxWidth: 320,
  },
  tdEmpty: { padding: 40, textAlign: 'center', color: '#94a3b8' },
  trActive: { background: '#f8fafc' },
  editorCell: { background: '#f8fafc', padding: '6px 14px 14px', borderBottom: '1px solid #e2e8f0' },
  editorBreadcrumb: { fontSize: 12, color: '#64748b', padding: '8px 0' },
  practiceCountBadge: {
    display: 'inline-block', marginLeft: 8, padding: '1px 6px',
    borderRadius: 8, background: '#e2e8f0', color: '#475569', fontSize: 10, fontWeight: 600,
  },
  badgeOk: {
    padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600,
    background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0',
  },
  badgeWarn: {
    padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600,
    background: '#fef9c3', color: '#854d0e', border: '1px solid #fde68a',
  },
  badgeMuted: {
    padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600,
    background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0',
  },
  btnSecondary: {
    padding: '6px 14px', background: '#f1f5f9', color: '#334155',
    border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer',
  },
  btnSecondaryActive: {
    padding: '6px 14px', background: '#2563eb', color: '#fff',
    border: '1px solid #2563eb', borderRadius: 6, fontSize: 12, cursor: 'pointer',
  },
};

export default IdentityConsole;
